// Questionnaire Version Management Utilities
// Provides helpful functions for managing questionnaire template versions

import { questionnaireVersions, CURRENT_QUESTIONNAIRE_VERSION } from './questionnaireTemplate';

/**
 * Validates if a given version exists in the version registry
 * @param {string} version - Version string to validate
 * @returns {boolean} - True if version exists
 */
export const isValidVersion = (version) => {
  return version && questionnaireVersions.hasOwnProperty(version);
};

/**
 * Gets the template for a specific version with validation
 * @param {string} version - Version string
 * @returns {Object|null} - Template object or null if invalid
 */
export const getValidatedTemplate = (version) => {
  if (!isValidVersion(version)) {
    console.warn(`Invalid questionnaire version requested: ${version}. Falling back to current version.`);
    return questionnaireVersions[CURRENT_QUESTIONNAIRE_VERSION];
  }
  return questionnaireVersions[version];
};

/**
 * Compares two version strings to determine which is newer
 * @param {string} version1 - First version string
 * @param {string} version2 - Second version string  
 * @returns {number} - 1 if version1 > version2, -1 if version1 < version2, 0 if equal
 */
export const compareVersions = (version1, version2) => {
  const v1Parts = version1.split('.').map(Number);
  const v2Parts = version2.split('.').map(Number);
  
  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
    const v1Part = v1Parts[i] || 0;
    const v2Part = v2Parts[i] || 0;
    
    if (v1Part > v2Part) return 1;
    if (v1Part < v2Part) return -1;
  }
  
  return 0;
};

/**
 * Gets the most recent version available
 * @returns {string} - Latest version string
 */
export const getLatestVersion = () => {
  const versions = Object.keys(questionnaireVersions);
  return versions.reduce((latest, current) => {
    return compareVersions(current, latest) > 0 ? current : latest;
  });
};

/**
 * Checks if a version is outdated compared to the current active version
 * @param {string} version - Version to check
 * @returns {boolean} - True if version is outdated
 */
export const isVersionOutdated = (version) => {
  return compareVersions(version, CURRENT_QUESTIONNAIRE_VERSION) < 0;
};

/**
 * Gets metadata about a specific template version
 * @param {string} version - Version string
 * @returns {Object} - Version metadata
 */
export const getVersionMetadata = (version) => {
  const template = getValidatedTemplate(version);
  if (!template) return null;
  
  return {
    version: version,
    title: template.title,
    status: template.status,
    fieldCount: template.item ? template.item.length : 0,
    isCurrent: version === CURRENT_QUESTIONNAIRE_VERSION,
    isOutdated: isVersionOutdated(version)
  };
};

/**
 * Gets a list of all available versions with metadata
 * @returns {Array} - Array of version metadata objects
 */
export const getAllVersionsMetadata = () => {
  return Object.keys(questionnaireVersions)
    .map(version => getVersionMetadata(version))
    .sort((a, b) => compareVersions(b.version, a.version)); // Sort newest first
};

/**
 * Validates questionnaire response data against a specific template version
 * @param {Object} responses - Questionnaire response data
 * @param {string} version - Template version to validate against
 * @returns {Object} - Validation result with isValid flag and missing fields
 */
export const validateResponsesForVersion = (responses, version) => {
  const template = getValidatedTemplate(version);
  if (!template) {
    return { isValid: false, error: 'Invalid template version' };
  }
  
  const missingFields = [];
  const validateSection = (items, responseData = responses) => {
    items.forEach(item => {
      if (item.type === 'group' && item.item) {
        if (item.repeats) {
          // For repeating groups, check if array exists
          const sectionData = responseData[item.linkId];
          if (Array.isArray(sectionData)) {
            sectionData.forEach(groupItem => validateSection(item.item, groupItem));
          }
        } else {
          validateSection(item.item, responseData);
        }
      } else {
        // For individual fields, check if response exists
        if (!responseData.hasOwnProperty(item.linkId)) {
          missingFields.push({
            linkId: item.linkId,
            text: item.text,
            type: item.type
          });
        }
      }
    });
  };
  
  validateSection(template.item);
  
  return {
    isValid: missingFields.length === 0,
    missingFields: missingFields,
    totalFields: countTemplateFields(template),
    completedFields: countTemplateFields(template) - missingFields.length
  };
};

/**
 * Counts the total number of fields in a template
 * @param {Object} template - Questionnaire template
 * @returns {number} - Total field count
 */
const countTemplateFields = (template) => {
  let count = 0;
  
  const countItems = (items) => {
    items.forEach(item => {
      if (item.type === 'group' && item.item) {
        countItems(item.item);
      } else {
        count++;
      }
    });
  };
  
  if (template.item) {
    countItems(template.item);
  }
  
  return count;
};
